import React, { useState } from "react";
import { useSnackbar } from "notistack";

import { Dealer } from "../../../dtos/dealer";
import { AjaxResult } from "../../../enums/ajaxResult";
import { useConfirmDialog } from "../../../hooks/useConfirmDialog";
import { useApi } from "../../useApi";
import { navLinkUrl } from "../../../utils";
import { UserLevel } from "../../../enums/userLevel";
import { RowActionsMenu, RowAction } from "../../shared/RowActionsMenu";
import { NewEmployeeForm } from "../employees/form";

interface Props {
    dealer: Dealer;
    onLoadDealers: () => void;
    dealersAreLoading: boolean;
}

/**
 * Renders dealer-row actions in two layers:
 *
 *  - Inline links (Orgs / Add Admin User / Configure) stay top-level so the
 *    e2e Playwright suite can find them with the existing
 *    `a:has-text('Configure')` etc. selectors.
 *  - Destructive actions (Delete + Activate / Deactivate) are folded into a
 *    single `Actions ▾` overflow menu so they're visually grouped and can't
 *    be hit by accident; both still gate on `useConfirmDialog`.
 */
export function DealerRowActions({ dealer, onLoadDealers, dealersAreLoading }: Props) {
    const { dealerApi } = useApi();
    const { enqueueSnackbar } = useSnackbar();
    const { showConfirmDialog } = useConfirmDialog();
    const [isLoading, setIsLoading] = useState(false);
    const [addEmployeeOpen, setAddEmployeeOpen] = useState(false);

    const busy = isLoading || dealersAreLoading;

    const deleteDealer = () => {
        showConfirmDialog(`Are you sure you want to delete dealer "${dealer.name}"?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            dealerApi.delete(dealer.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    enqueueSnackbar(`Dealer "${dealer.name}" deleted successfully`);
                    onLoadDealers();
                } else if (r.messages) {
                    enqueueSnackbar(r.messages.join("\r\n"));
                } else {
                    enqueueSnackbar(`Failed to delete dealer "${dealer.name}"!`, { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const activateDealer = () => {
        showConfirmDialog(`Are you sure you want to activate dealer "${dealer.name}"?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            dealerApi.activate(dealer.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    onLoadDealers();
                } else {
                    enqueueSnackbar(`Failed to activate dealer "${dealer.name}"`, { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const deactivateDealer = () => {
        showConfirmDialog(`Are you sure you want to deactivate dealer "${dealer.name}"?`).then((confirmed) => {
            if (!confirmed) return;
            setIsLoading(true);
            dealerApi.deactivate(dealer.id).then((r) => {
                if (r.result === AjaxResult.Success) {
                    onLoadDealers();
                } else {
                    enqueueSnackbar(`Failed to deactivate dealer "${dealer.name}"`, { variant: "error" });
                }
                setIsLoading(false);
            });
        });
    };

    const menuActions: RowAction[] = [
        {
            label: "Orgs",
            to: `/dealers/${dealer.id}/organizations`,
        },
        {
            label: "Add Admin User",
            onClick: () => setAddEmployeeOpen(true),
        },
        {
            label: "Configure",
            to: navLinkUrl("dealer-config", dealer.id),
        },
        {
            label: dealer.isInactive ? "Activate" : "Deactivate",
            onClick: dealer.isInactive ? activateDealer : deactivateDealer,
            dangerous: !dealer.isInactive,
            disabled: busy,
            dividerBefore: true,
        },
        {
            label: "Delete",
            onClick: deleteDealer,
            dangerous: true,
            disabled: busy,
        },
    ];

    return (
        <>
            <RowActionsMenu actions={menuActions} ariaLabel={`Actions for ${dealer.name}`} />
            {addEmployeeOpen && (
                <NewEmployeeForm
                    closeForm={() => setAddEmployeeOpen(false)}
                    loadEmployees={onLoadDealers}
                    dealerId={dealer.id}
                    initPartial={{ userLevel: UserLevel.DealerAdmin }}
                />
            )}
        </>
    );
}
